/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { cssProps } from '@oracle/oraclejet-cssprops';

export type Props = {
  /**
   * Sets the variant for the badge. Badge can be subtle or solid with different colors.
   */
  variant?:
    | 'neutral'
    | 'neutralSubtle'
    | 'danger'
    | 'dangerSubtle'
    | 'success'
    | 'successSubtle'
    | 'warning'
    | 'warningSubtle'
    | 'info'
    | 'infoSubtle';
  /**
   * Specifies the size of the badge. Consists of two options: Medium and small.
   */
  size?: 'sm' | 'md';
  /**
   * Specifies the edge of the badge. Badges can be attached to the end edge of another component. They lose their default corner rounding on right side for ltr direction or left side for rtl direction.
   */
  edge?: 'none' | 'end';
  /**
   * Specifies the string that will be rendered in the badge.
   */
  children: string;
};

const styles = {
  base: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color)',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-text-color)',
    display: 'inline-block',
    paddingTop: '0',
    paddingBottom: '0',
    paddingLeft: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-padding)',
    paddingRight: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-padding)',
    boxSizing: 'border-box',
    borderTopLeftRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-border-radius)',
    borderTopRightRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-border-radius)',
    borderBottomRightRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-border-radius)',
    borderBottomLeftRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-border-radius)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-height)',
    lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-height)',
    minWidth: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-height)',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-font-size)',
    fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-font-weight)',
    fontStretch: 'var(--oj-c-PRIVATE-DO-NOT-USE-badge-font-stretch)',
    overflowX: 'hidden',
    overflowY: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textAlign: 'center',
    verticalAlign: 'middle'
  }}`,

  //right-aligned with the right edge of another component for ltr direction
  //left-aligned for rtl direction pending by
  //JET-47572: need an api for rtl
  end: cssProps`${{
    borderTopRightRadius: '0',
    borderBottomRightRadius: '0'
  }}`,

  neutral: cssProps`${{
    /* solid neutral badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-neutral-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-neutral-contrast)'
  }}`,
  danger: cssProps`${{
    /* solid danger badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-danger-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-danger-contrast)'
  }}`,
  warning: cssProps`${{
    /* solid warning badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-warning-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-warning-contrast)'
  }}`,
  success: cssProps`${{
    /* solid success badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-success-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-success-contrast)'
  }}`,
  info: cssProps`${{
    /* solid info badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-contrast)'
  }}`,

  neutralSubtle: cssProps`${{
    /* subtle neutral badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-neutral-secondary-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-neutral-secondary-contrast)'
  }}`,
  dangerSubtle: cssProps`${{
    /* subtle danger badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-danger-secondary-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-danger-secondary-contrast)'
  }}`,
  warningSubtle: cssProps`${{
    /* subtle warning badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-warning-secondary-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-warning-secondary-contrast)'
  }}`,
  successSubtle: cssProps`${{
    /* subtle success badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-success-secondary-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-success-secondary-contrast)'
  }}`,
  infoSubtle: cssProps`${{
    /* subtle info badges */
    '--oj-c-PRIVATE-DO-NOT-USE-badge-bg-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-secondary-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-badge-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-info--secondarycontrast)'
  }}`,

  childrenMargins: cssProps`${{
    /* @ts-ignore */
    '& > *': {
      marginTop: '0 !important',
      marginBottom: '0 !important',
      marginLeft: '0 !important',
      marginRight: '0 !important'
    }
  }}`
};

export function Badge({ variant = 'neutral', size = 'md', edge = 'none', children }: Props) {
  const textClasses = classNames([
    styles.base,
    size === 'sm' && 'oj-badge-sm',
    edge === 'end' && styles.end,
    styles[variant],
    styles.childrenMargins
  ]);
  return <span class={textClasses}>{children}</span>;
}
